import { CSSProperties, ReactNode } from 'react'
import useScreen from 'Hooks/useScreen'
import { styx } from 'modules/Utils'
import Colors from 'modules/Colors'
import Sty from 'modules/Sty'
import Icon from 'elements/Icon'

// Centralized workspace color theme - easily updateable
const WorkspaceTheme = {
    // Backgrounds
    primary: Colors.purpleDark,           // Main dark background
    secondary: '#1A1625',                 // Slightly lighter dark background  
    surface: '#241D30',                   // Card/surface background
    surfaceLight: '#2D2438',              // Lighter surface for headers
    
    // Borders
    border: '#3D3447',                    // Main border color
    borderLight: '#332A40',               // Lighter border
    
    // Text
    textPrimary: '#FFFFFF',               // Primary text (white)
    textSecondary: '#B8B3C7',             // Secondary text (light purple-grey)
    textTertiary: '#8B8199',              // Tertiary text (darker purple-grey)
    
    // Accents
    accent: Colors.purple,                // Main purple accent
    accentLight: Colors.purpleLight,      // Light purple accent
    accentSoft: Colors.purpleLighter,     // Very light purple for subtle highlights
    
    // States
    hover: '#342B40',                     // Hover state background
    selected: Colors.purpleTransparent,   // Selected state background
    loading: Colors.purpleLight,          // Loading state color
}

interface AssistantWorkspaceWrapperProps {
    title: string;
    icon?: string;
    isLoading?: boolean;
    children?: ReactNode;
    onClose?: () => void;
    className?: string;
}

const AssistantWorkspaceWrapper = ({
    title,
    icon = 'website',
    isLoading = false,
    children,
    onClose,
    className
}: AssistantWorkspaceWrapperProps) => {
    const { mobileView, mStyle } = useScreen()

    // Check if this is being used in modal context
    const isModal = className?.includes('modalWorkspace')

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="assistantWorkspaceLoadingState" style={sty.loadingState}>
                    <Icon 
                        className="assistantWorkspaceLoadingIcon"
                        icon="maestroBadgeImage" 
                        size={48} 
                        style={sty.loadingIcon}
                    />
                    <div style={sty.loadingText}>Loading...</div>
                </div>
            )
        }
        return children
    }

    return (
        <div 
            className={`assistantWorkspaceWrapper ${className || ''}`} 
            style={styx(sty.wrapper, isModal && sty.wrapper_modal)}
        >
            <div className="assistantWorkspaceHeader" style={sty.header}>
                <div className="assistantWorkspaceTitle" style={sty.title}>
                    <Icon icon={icon} size={20} className="assistantWorkspaceIcon" />
                    {title}
                </div>
                {onClose && (
                    <button
                        className="assistantWorkspaceCloseButton"
                        style={sty.closeButton}
                        onClick={onClose}
                        type="button"
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = WorkspaceTheme.hover
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent'
                        }}
                    >
                        <Icon icon="close" size={16} className="assistantWorkspaceCloseIcon" />
                    </button>
                )}
            </div>
            <div 
                className="assistantWorkspaceContent" 
                style={mStyle(sty, "assistantWorkspaceContent")}
            >
                {renderContent()}
            </div>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: WorkspaceTheme.primary,
        borderRadius: 8,
        border: `1px solid ${WorkspaceTheme.border}`,
        overflow: 'hidden',
    },
    wrapper_mobile: {
        width: '100%',
        minWidth: 0,
        borderRadius: 0,
    },
    wrapper_modal: {
        border: 'none',
        borderRadius: 0,
        height: '100%',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 16,
        borderBottom: `1px solid ${WorkspaceTheme.border}`,
        backgroundColor: WorkspaceTheme.surfaceLight,
        flexShrink: 0,
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 16,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
    },
    closeButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: 4,
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        color: WorkspaceTheme.textSecondary,
    },
    assistantWorkspaceContent: {
        flex: 1,
        padding: 16,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: WorkspaceTheme.primary,
        width: '100%',
        boxSizing: 'border-box',
    },
    assistantWorkspaceContent_mobile: {
        padding: 0,
        width: '100%',
    },
    loadingState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: 16,
    },
    loadingIcon: {
        color: WorkspaceTheme.loading,
        animation: 'spin 2s linear infinite',
    },
    loadingText: {
        fontSize: 16,
        color: WorkspaceTheme.textSecondary,
        textAlign: 'center',
    },
}

// Export the theme for use in other workspace components
export { WorkspaceTheme }
export default AssistantWorkspaceWrapper 